import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  Alert,
  Stack,
  Button,
  Dialog,
  TextField,
  AlertTitle,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { PurgePreview } from 'src/services/admin/admin.types';
import {
  usePurgeTenantMutation,
  useLazyGetPurgePreviewQuery,
} from 'src/services/admin/admin.service';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  organizationId: string;
  companyName?: string | null;
};

/**
 * Permanent deletion of a tenant created in error or fraudulently.
 *
 * Shows the blast radius first - every table and row count that would go, and which users are kept
 * because they also belong elsewhere - and requires the organization id to be typed back before the
 * delete button unlocks. Once purged, the tenant's email addresses and domain are free to reuse.
 */
export default function PurgeTenantDialog({ open, onClose, organizationId, companyName }: Props) {
  const { t } = useTranslate();
  const router = useRouter();

  const [confirmation, setConfirmation] = useState('');
  const [preview, setPreview] = useState<PurgePreview | null>(null);

  const [getPreview, { isFetching }] = useLazyGetPurgePreviewQuery();
  const [purgeTenant, { isLoading: isPurging }] = usePurgeTenantMutation();

  useEffect(() => {
    if (!open) {
      setConfirmation('');
      setPreview(null);
      return;
    }

    getPreview({ id: organizationId })
      .unwrap()
      .then(setPreview)
      .catch(() => {
        enqueueSnackbar(t('restricted.organizations.purge.api.preview_error'), { variant: 'error' });
      });
  }, [open, organizationId, getPreview, t]);

  const blocked = (preview?.blockers.length ?? 0) > 0;
  const canPurge = !!preview && !blocked && confirmation.trim() === organizationId;

  const handlePurge = async () => {
    try {
      const result = await purgeTenant({ id: organizationId, confirmation }).unwrap();

      enqueueSnackbar(
        t('restricted.organizations.purge.api.success', { count: result.total_rows }),
        { variant: 'success' }
      );

      onClose();
      router.push(paths.dashboard.restricted.tenants.root);
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ?? t('restricted.organizations.purge.api.default_error'),
        { variant: 'error' }
      );
    }
  };

  const renderPreview = () => {
    if (!preview) return null;

    const tables = Object.entries(preview.tables).sort((a, b) => b[1] - a[1]);

    return (
      <Stack spacing={2}>
        {blocked && (
          <Alert severity="error">
            <AlertTitle>{t('restricted.organizations.purge.blocked_title')}</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preview.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            color="error"
            label={t('restricted.organizations.purge.total_rows', { count: preview.total_rows })}
          />
          <Chip
            color="error"
            variant="outlined"
            label={t('restricted.organizations.purge.users_deleted', { count: preview.users_to_delete })}
          />
          {preview.users_retained > 0 && (
            <Chip
              variant="outlined"
              label={t('restricted.organizations.purge.users_retained', { count: preview.users_retained })}
            />
          )}
        </Stack>

        {tables.length > 0 && (
          <Box
            sx={{
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Stack spacing={0.5}>
              {tables.map(([table, count]) => (
                <Stack key={table} direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{table}</Typography>
                  <Typography variant="caption">{count}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: 'error.main' }}>
        {t('restricted.organizations.purge.title')}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            <AlertTitle>
              {companyName ?? organizationId}
            </AlertTitle>
            {t('restricted.organizations.purge.warning')}
          </Alert>

          {isFetching ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            renderPreview()
          )}

          <TextField
            fullWidth
            size="small"
            label={t('restricted.organizations.purge.confirmation_label')}
            helperText={t('restricted.organizations.purge.confirmation_helper', { id: organizationId })}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            disabled={blocked || isFetching}
            inputProps={{ 'data-testid': 'purge-confirmation-input' }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton
          variant="contained"
          color="error"
          onClick={handlePurge}
          loading={isPurging}
          disabled={!canPurge}
          data-testid="purge-confirm-btn"
        >
          {t('restricted.organizations.purge.submit')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
